import polygonClipping from 'polygon-clipping';
import * as THREE from 'three';

// used for camera type

/** Absolute area of a 2D polygon using the shoelace formula. */
function polyArea(pts: [number, number][]): number {
  if (pts.length < 3) return 0;
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    a += pts[i][0] * pts[j][1] - pts[j][0] * pts[i][1];
  }
  return Math.abs(a / 2);
}

/** Perimeter of a 2D polygon. */
function polyPerim(pts: [number, number][]): number {
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    s += Math.hypot(pts[j][0] - pts[i][0], pts[j][1] - pts[i][1]);
  }
  return s;
}

/** Area of a polygon with holes. Outer area minus sum of hole areas. */
export function polyAreaWithHoles(outer: [number, number][], holes?: [number, number][][]): number {
  let area = polyArea(outer);
  if (holes) {
    for (const hole of holes) {
      area -= polyArea(hole);
    }
  }
  return Math.max(0, area);
}

/** Perimeter of a polygon including hole perimeters. */
export function polyPerimWithHoles(outer: [number, number][], holes?: [number, number][][]): number {
  let perim = polyPerim(outer);
  if (holes) {
    for (const hole of holes) {
      perim += polyPerim(hole);
    }
  }
  return perim;
}

/** Snap a world coordinate to the nearest grid point. */
export function snapToGrid(x: number, y: number, gridSize: number): [number, number] {
  return [Math.round(x / gridSize) * gridSize, Math.round(y / gridSize) * gridSize];
}

/** Convert screen (pixel) coordinates to world coordinates for an orthographic camera.
 *  Uses direct frustum math (identity rotation assumed) — matches the prototype's s2w(). */
export function screenToWorld(
  screenX: number,
  screenY: number,
  camera: THREE.OrthographicCamera,
  canvas: HTMLCanvasElement,
): [number, number] {
  const rect = canvas.getBoundingClientRect();
  // NDC: -1..+1 horizontally (left→right), -1..+1 vertically (bottom→top)
  const nx = ((screenX - rect.left) / rect.width) * 2 - 1;
  const ny = -((screenY - rect.top) / rect.height) * 2 + 1;

  const wx = camera.position.x + (nx * (camera.right - camera.left)) / 2;
  const wy = camera.position.y + (ny * (camera.top - camera.bottom)) / 2;
  return [wx, wy];
}

// ── Circle ──────────────────────────────────────────────────────────────────

/** Generate N evenly-spaced points on a circle (no duplicate closing point). */
export function circlePoints(cx: number, cy: number, radius: number, segments: number = 48): [number, number][] {
  const pts: [number, number][] = [];
  for (let i = 0; i < segments; i++) {
    const angle = (2 * Math.PI * i) / segments;
    pts.push([cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius]);
  }
  return pts;
}

// ── Point-in-polygon (ray casting) ──────────────────────────────────────────

/** Test if a point (x, y) lies inside a polygon using ray-casting. */
export function pointInPolygon(x: number, y: number, polygon: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0],
      yi = polygon[i][1];
    const xj = polygon[j][0],
      yj = polygon[j][1];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

// ── Subtract / Boolean ──────────────────────────────────────────────────────

/**
 * Find which polygon (by index) contains the subtract polygon.
 * Checks centroid first, then individual vertices as fallback.
 * Returns -1 if no overlap found. Searches from top (last) to bottom.
 */
export function findOverlappingPolygon(
  polygons: { points: [number, number][] }[],
  subtractPoints: [number, number][],
): number {
  // Centroid of subtract polygon
  const cx = subtractPoints.reduce((s, p) => s + p[0], 0) / subtractPoints.length;
  const cy = subtractPoints.reduce((s, p) => s + p[1], 0) / subtractPoints.length;

  // Check centroid (most common case)
  for (let i = polygons.length - 1; i >= 0; i--) {
    if (pointInPolygon(cx, cy, polygons[i].points)) return i;
  }

  // Fallback: check each vertex
  for (const [sx, sy] of subtractPoints) {
    for (let i = polygons.length - 1; i >= 0; i--) {
      if (pointInPolygon(sx, sy, polygons[i].points)) return i;
    }
  }

  return -1;
}

/** Remove closing duplicate point if present (polygon-clipping may add one). */
function cleanRing(ring: [number, number][]): [number, number][] {
  if (ring.length >= 2) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) {
      return ring.slice(0, -1);
    }
  }
  return ring;
}

/**
 * Subtract a polygon from another polygon (with optional existing holes).
 * Uses polygon-clipping library for boolean difference.
 * Returns updated points + holes, or null if subtraction produces no result.
 */
export function subtractPolygon(
  outerPoints: [number, number][],
  existingHoles: [number, number][][] | undefined,
  cutPoints: [number, number][],
): { points: [number, number][]; holes?: [number, number][][] } | null {
  // Build subject polygon: [outerRing, ...holeRings]
  const subjectRings: [number, number][][] = [outerPoints.map((p) => [...p])];
  if (existingHoles) {
    for (const hole of existingHoles) {
      subjectRings.push(hole.map((p) => [...p]));
    }
  }

  const clipRings: [number, number][][] = [cutPoints.map((p) => [...p])];

  try {
    const result = polygonClipping.difference([subjectRings], [clipRings]);

    if (result.length === 0) return null; // Complete erasure

    // Take the largest resulting polygon (subtraction may split)
    let bestIdx = 0;
    let bestArea = 0;
    for (let i = 0; i < result.length; i++) {
      const area = polyArea(result[i][0] as [number, number][]);
      if (area > bestArea) {
        bestArea = area;
        bestIdx = i;
      }
    }

    const bestPoly = result[bestIdx];
    const newOuter = cleanRing(bestPoly[0] as [number, number][]);
    const newHoles = bestPoly.slice(1).map((ring) => cleanRing(ring as [number, number][]));

    return {
      points: newOuter,
      holes: newHoles.length > 0 ? newHoles : undefined,
    };
  } catch (err) {
    console.warn('Polygon subtraction failed:', err);
    return null;
  }
}

/**
 * Split a polygon into multiple pieces using a cut polygon.
 * Returns ALL resulting pieces (not just the largest).
 */
export function splitPolygon(
  outerPoints: [number, number][],
  existingHoles: [number, number][][] | undefined,
  cutPoints: [number, number][],
): { points: [number, number][]; holes?: [number, number][][] }[] | null {
  const subjectRings: [number, number][][] = [outerPoints.map((p) => [...p])];
  if (existingHoles) {
    for (const hole of existingHoles) {
      subjectRings.push(hole.map((p) => [...p]));
    }
  }
  const clipRings: [number, number][][] = [cutPoints.map((p) => [...p])];
  try {
    const result = polygonClipping.difference([subjectRings], [clipRings]);
    if (result.length === 0) return null;
    return result.map((poly) => {
      const outer = cleanRing(poly[0] as [number, number][]);
      const holes = poly.slice(1).map((ring) => cleanRing(ring as [number, number][]));
      return { points: outer, holes: holes.length > 0 ? holes : undefined };
    });
  } catch (err) {
    console.warn('Polygon split failed:', err);
    return null;
  }
}

// ── THREE.Shape helpers ─────────────────────────────────────────────────────

/** Build a THREE.Shape from polygon points with optional holes. */
export function buildShapeWithHoles(points: [number, number][], holes?: [number, number][][]): THREE.Shape {
  const shape = new THREE.Shape();
  shape.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i++) {
    shape.lineTo(points[i][0], points[i][1]);
  }
  shape.closePath();

  if (holes && holes.length > 0) {
    for (const hole of holes) {
      const holePath = new THREE.Path();
      holePath.moveTo(hole[0][0], hole[0][1]);
      for (let i = 1; i < hole.length; i++) {
        holePath.lineTo(hole[i][0], hole[i][1]);
      }
      holePath.closePath();
      shape.holes.push(holePath);
    }
  }

  return shape;
}
